// AnchoredWallGridVisualizer — draws a gravity-aligned grid mesh at
// the current anchored wall frame published by the WebXR driver. The
// grid is a flat 2m × 2m plane, 20 × 20 cells, whose local axes align
// with the anchor's (gravityRight, gravityUp, normal). Result: lines
// on the grid read as horizontal + vertical relative to real-world
// gravity, so the surface looks like a real wall with an overlay ruler.
//
// Not part of the wheel renderer — this is a standalone diagnostic
// layer wired up from main.ts. Subscribes to the driver's plane store
// (which also fires on sessionInfo changes); re-poses the grid only
// when currentAnchoredWall reference changes.

import * as THREE from 'three'
import type {
  AnchoredPlaneInfo,
  DetectedPlanesStore,
  WebXRSessionInfo,
} from '@/ar/webxr-driver'

const GRID_SIZE_M = 2
const GRID_DIVISIONS = 20

export class AnchoredWallGridVisualizer {
  private readonly scene: THREE.Scene
  private readonly getSessionInfo: () => WebXRSessionInfo
  private readonly unsubscribe: () => void
  private readonly grid: THREE.LineSegments
  private lastAnchor: AnchoredPlaneInfo | null = null

  constructor(
    scene: THREE.Scene,
    planes: DetectedPlanesStore,
    getSessionInfo: () => WebXRSessionInfo,
  ) {
    this.scene = scene
    this.getSessionInfo = getSessionInfo
    this.grid = buildGrid()
    this.grid.visible = false
    scene.add(this.grid)
    this.sync()
    this.unsubscribe = planes.subscribe(() => this.sync())
  }

  private sync(): void {
    const anchor = this.getSessionInfo().currentAnchoredWall
    if (anchor === this.lastAnchor) return
    this.lastAnchor = anchor
    if (anchor == null) {
      this.grid.visible = false
      return
    }
    this.grid.visible = true
    this.grid.position.set(anchor.position.x, anchor.position.y, anchor.position.z)
    // Build a rotation matrix whose columns are the anchor's tangent-
    // frame basis vectors: (gravityRight, gravityUp, normal). Applying
    // that rotation to the grid's local axes (X, Y, Z) puts the grid
    // flat on the wall with its lines running horizontal + vertical.
    const m = new THREE.Matrix4().makeBasis(
      new THREE.Vector3(anchor.gravityRight.x, anchor.gravityRight.y, anchor.gravityRight.z),
      new THREE.Vector3(anchor.gravityUp.x, anchor.gravityUp.y, anchor.gravityUp.z),
      new THREE.Vector3(anchor.normal.x, anchor.normal.y, anchor.normal.z),
    )
    this.grid.quaternion.setFromRotationMatrix(m)
  }

  dispose(): void {
    this.unsubscribe()
    this.scene.remove(this.grid)
    this.grid.geometry.dispose()
    ;(this.grid.material as THREE.Material).dispose()
  }
}

/** Builds a flat XY-plane grid of GRID_DIVISIONS × GRID_DIVISIONS cells
 *  centered at the origin, spanning ±GRID_SIZE_M/2 on each axis. Grid
 *  lies in Z=0 so the LineSegments' local +Z is the surface normal — the
 *  visualizer rotates that into the anchor frame each frame. */
function buildGrid(): THREE.LineSegments {
  const half = GRID_SIZE_M / 2
  const step = GRID_SIZE_M / GRID_DIVISIONS
  const positions: number[] = []
  for (let i = 0; i <= GRID_DIVISIONS; i++) {
    const t = -half + i * step
    // Vertical line at x=t, running from y=-half to y=+half
    positions.push(t, -half, 0, t, half, 0)
    // Horizontal line at y=t, running from x=-half to x=+half
    positions.push(-half, t, 0, half, t, 0)
  }
  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute(
    'position',
    new THREE.BufferAttribute(new Float32Array(positions), 3),
  )
  const material = new THREE.LineBasicMaterial({
    color: 0x00ff88,
    transparent: true,
    opacity: 0.5,
    depthTest: false,
  })
  const grid = new THREE.LineSegments(geometry, material)
  grid.name = 'anchored-wall-grid'
  grid.renderOrder = 998
  return grid
}
